import {
  MonitoreoCultivoPlagaItem,
  MonitoreoPlagaEnfermedadItem,
} from "../../../generated/grpc/catalogos_monitoreo_pb";
import { MonitoreoReferencia } from "../../../generated/grpc/monitoreo_sesion_pb";
import { DatosMonitoreo, LoteMonitoreado } from "./agruparPorSemana";
import {
  ESTADO_LABEL as ESTADO_LABEL_EVALUACION,
  ESTADO_RANK as ESTADO_RANK_EVALUACION,
  Evaluacion,
  evaluar,
  MonitoreoEstado as MonitoreoEstadoEvaluacion,
  ResultadoOrganismo,
} from "./evaluacion";

/** Estado del semáforo. Lo define {@link ./evaluacion}, que es el motor; acá se
 *  reexporta para no romper los imports que ya existen. */
export type MonitoreoEstado = MonitoreoEstadoEvaluacion;

/**
 * Plagas que se están mirando: `null` = todas las de la finca (modo "Todas"),
 * una lista = solo esas.
 *
 * Un solo tipo para los tres modos del mapa y no un enum de modos, porque la
 * regla es siempre la misma —el lote toma el **peor estado entre las plagas
 * seleccionadas**— y los modos son casos particulares de esa única regla:
 * "Todas" es la selección sin recortar, un grupo del catálogo ("Ácaro") una
 * lista de varias, y una plaga concreta la lista de una.
 */
export type SeleccionPlagas = string[] | null;

/** Peso de cada estado para ordenar por severidad (mayor = peor). */
export const ESTADO_RANK = ESTADO_RANK_EVALUACION;

/** Etiqueta visible de cada estado. */
export const ESTADO_LABEL = ESTADO_LABEL_EVALUACION;

/**
 * Estado de una incidencia contra el umbral de su plaga.
 *
 * Una plaga marcada `isCritico` no tiene gradiente: cualquier detección
 * (`valor > 0`) es alerta. Para el resto se compara contra `rangoMedio`
 * (alerta) y `rangoBajo` (precaución); un rango en 0 significa "sin umbral
 * definido" y se ignora, porque si no toda incidencia caería en alerta.
 *
 * `MonitoreoPlagaEnfermedadItem.logicaUmbral` no se consulta, y está bien que no
 * se consulte: SEXTANTE confirmó (2026-10-20) que **no existe ninguna plaga ni
 * indicador donde "menos sea peor"**. Todas se evalúan igual —más individuos,
 * peor—, así que no hay caso invertido que atender.
 *
 * El campo, pese al nombre, **no es una bandera de lógica**: es texto libre de
 * 5000 caracteres con notas agronómicas (método, herramienta, etapa crítica,
 * frecuencia; ver la migración `MonitoreoPapaPlagas` del server). Acá había un
 * pendiente que advertía que una plaga con lógica invertida se evaluaría al
 * revés: era una falsa alarma nacida de ese nombre, y queda cerrada.
 */

/**
 * Valores de referencia **vigentes** de un organismo en un cultivo, indexados por
 * `cultivoId|plagaId`.
 *
 * Lo cargan las pantallas desde `monitoreoCultivoPlagaGrpcCatalogService` y lo
 * dejan acá con {@link registrarTablaPorCultivo}.
 *
 * ⚠️ **Es un registro de módulo y no un parámetro, a conciencia.** Pasarlo como
 * argumento obligaría a cambiar la firma de las cuatro funciones de lote y sus
 * ocho consumidores, para algo que solo hace falta en las sesiones anteriores al
 * congelado —y que `rellenarReferencias` va reduciendo—. La especificación lo
 * plantea en esos mismos términos: *"usar los valores vigentes del cultivo del
 * lote **si la pantalla los tiene**"* (5.2, última fila). Si la pantalla no los
 * cargó, el estado queda `null`, que es exactamente lo que dice esa frase.
 */
let _tablaPorCultivo: Map<string, MonitoreoCultivoPlagaItem> = new Map();

/** Clave del índice. El organismo solo: el mismo ácaro en limón y en naranja son
 *  dos filas con valores distintos, y mezclarlas es el bug que la tabla por
 *  cultivo vino a arreglar. */
function _clave(cultivoId: string, plagaId: string): string {
  return `${cultivoId}|${plagaId}`;
}

/**
 * Carga la tabla por cultivo para el camino de compatibilidad.
 *
 * Se llama una vez por pantalla, después de traer el catálogo. Pasarle una lista
 * vacía no es un error: significa que la tabla todavía no está cargada en la base
 * —hoy es el caso, hasta que se cargue la librería v4— y entonces se sigue
 * cayendo al catálogo viejo.
 */
export function registrarTablaPorCultivo(
  items: MonitoreoCultivoPlagaItem[],
): void {
  _tablaPorCultivo = new Map(
    items
      .filter((item) => !item.eliminado)
      .map((item) => [_clave(item.cultivoId, item.plagaEnfermedadId), item]),
  );
}

/**
 * Los valores vigentes con los que evaluar un organismo cuando la sesión no trae
 * su copia congelada (especificación 5.2, última fila).
 *
 * Orden de preferencia, y el motivo de cada paso:
 *
 * 1. **La tabla por cultivo.** Es la respuesta correcta: tiene la unidad y los
 *    cuatro umbrales del organismo *en ese cultivo*.
 * 2. **El catálogo viejo**, si la tabla no tiene esa fila. ⚠️ Sus
 *    `rangoBajo`/`rangoMedio` son **un solo umbral compartido** por limón,
 *    naranja y mandarina —la fila "Cítricos"—, así que un lote de naranja se
 *    evalúa con el umbral del limón. Es peor que lo correcto y mejor que nada:
 *    sin este paso, **hoy** el semáforo del histórico entero se apagaría, porque
 *    la tabla por cultivo está vacía hasta que se cargue la librería v4.
 * 3. `null`: no se puede evaluar, y se muestra sin color.
 *
 * El paso 2 se borra cuando la v4 esté cargada para todos los cultivos. Hasta
 * entonces es la razón por la que quedan dos lecturas de `rangoBajo`/`rangoMedio`
 * en el proyecto.
 */
function _referenciaVigente(
  cultivoId: string | undefined,
  plagaId: string,
  plaga: MonitoreoPlagaEnfermedadItem | undefined,
): MonitoreoReferencia | null {
  if (cultivoId) {
    const fila = _tablaPorCultivo.get(_clave(cultivoId, plagaId));
    if (fila) {
      return {
        unidad: fila.unidad,
        esCritico: plaga?.isCritico ?? false,
        grupoId: plaga?.grupoId,
        nivelBajoMax: fila.nivelBajoMax,
        nivelMedioMax: fila.nivelMedioMax,
        incidenciaBajoMax: fila.incidenciaBajoMax,
        incidenciaMedioMax: fila.incidenciaMedioMax,
        // 0 = no es un ajuste del cliente, son los valores por defecto del cultivo.
        referenciaVersion: 0,
      } as MonitoreoReferencia;
    }
  }

  if (!plaga) return null;
  return {
    // El catálogo viejo no distingue unidades: todo es individuos por árbol, que
    // es lo que `rangoBajo`/`rangoMedio` siempre midieron.
    unidad: "INDIVIDUOS_ARBOL",
    esCritico: plaga.isCritico,
    grupoId: plaga.grupoId,
    nivelBajoMax: plaga.rangoBajo > 0 ? plaga.rangoBajo : undefined,
    nivelMedioMax: plaga.rangoMedio > 0 ? plaga.rangoMedio : undefined,
    referenciaVersion: 0,
  } as MonitoreoReferencia;
}

/**
 * Lo medido de un organismo en un lote, listo para {@link evaluar}.
 *
 * Prefiere la **referencia congelada** del lote; solo cae al catálogo cuando la
 * sesión es anterior al congelado (ver {@link _referenciaVigente}).
 *
 * Devuelve `null` si el lote no está en `datos`: eso significa **no
 * monitoreado**, que no es lo mismo que monitoreado en cero.
 *
 * @param lote     El lote de `datos[loteId]`, o `undefined` si no está.
 * @param plagaId  Id del organismo.
 * @param plaga    Fila del catálogo, solo para el puente de compatibilidad.
 */
export function resolverResultado(
  lote: LoteMonitoreado | undefined,
  plagaId: string,
  plaga?: MonitoreoPlagaEnfermedadItem,
): ResultadoOrganismo | null {
  if (!lote) return null;
  return {
    // Una plaga ausente del mapa del lote es un cero medido —el monitor la buscó
    // y no estaba—, no un dato faltante: el lote está en `datos`, así que se
    // recorrió.
    promedio: lote.inc[plagaId] ?? 0,
    afectados: lote.afectados[plagaId] ?? 0,
    muestra: lote.muestra,
    referencia: lote.referencias[plagaId] ??
      _referenciaVigente(lote.cultivoId, plagaId, plaga),
  };
}

/**
 * Evalúa un organismo en un lote. Es el reemplazo de `getEstado`: toma el lote
 * entero en vez de un solo número, porque la incidencia necesita `afectados` y
 * `muestra`, y el umbral sale de la referencia congelada.
 */
export function evaluarEnLote(
  lote: LoteMonitoreado | undefined,
  plagaId: string,
  plaga?: MonitoreoPlagaEnfermedadItem,
): Evaluacion | null {
  const resultado = resolverResultado(lote, plagaId, plaga);
  return resultado === null ? null : evaluar(resultado);
}

/** La peor plaga de un lote, ya evaluada. */
export type PeorPlagaEvaluada = {
  plaga: MonitoreoPlagaEnfermedadItem;
  /** La evaluación completa. Lleva el nivel en su unidad, la incidencia y el
   *  estado: quien la muestra ya no tiene que recalcular nada. */
  evaluacion: Evaluacion;
  estado: MonitoreoEstado;
};

/**
 * Cuál es la plaga que define el estado del lote — no solo cuál es ese estado.
 * La necesita el modo "Todas" del mapa: el polígono se pinta con el peor estado,
 * y la etiqueta tiene que poder decir de qué plaga viene, o el color no se puede
 * accionar.
 *
 * Reemplaza a `getPeorPlagaLote`. Dos diferencias de fondo:
 *
 * 1. **Recorre la selección, no solo las plagas detectadas.** Un organismo
 *    ausente del mapa del lote es un cero medido, y un cero puede ser el peor
 *    estado que haya si todos los demás también están en cero. La versión vieja
 *    recorría `inc` y una plaga en cero ni se evaluaba.
 * 2. **Desempata con `Evaluacion.proporcion`**, que normaliza contra el umbral
 *    de alerta. Es la única forma de comparar organismos con unidades distintas:
 *    0,3 ácaros por lupada y 30 individuos por árbol no se ordenan por el número
 *    crudo, que es lo que hacía `_getProporcionUmbral`.
 *
 * Devuelve `null` cuando no hay nada que mostrar: el lote no se monitoreó, o
 * ninguna plaga de la selección se pudo evaluar.
 */
export function getPeorPlagaEvaluada(
  lote: LoteMonitoreado | undefined,
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
  seleccion: SeleccionPlagas = null,
): PeorPlagaEvaluada | null {
  if (!lote) return null;

  const candidatos =
    !seleccion || seleccion.length === 0
      ? [...new Set([...Object.keys(lote.inc), ...Object.keys(lote.referencias)])]
      : seleccion;

  let peor: PeorPlagaEvaluada | null = null;

  for (const plagaId of candidatos) {
    const plaga = plagasById.get(plagaId);
    if (!plaga) continue;

    const evaluacion = evaluarEnLote(lote, plagaId, plaga);
    // `estado` nulo es "no se pudo evaluar" -sin referencia, o benéfico-, y eso
    // no puede definir el color de un lote.
    if (evaluacion === null || evaluacion.estado === null) continue;

    const gana =
      peor === null ||
      ESTADO_RANK[evaluacion.estado] > ESTADO_RANK[peor.estado] ||
      (ESTADO_RANK[evaluacion.estado] === ESTADO_RANK[peor.estado] &&
        evaluacion.proporcion > peor.evaluacion.proporcion);

    if (gana) peor = { plaga, evaluacion, estado: evaluacion.estado };
  }

  return peor;
}

/**
 * Estado con el que se pinta un lote: LA regla del semáforo a nivel de lote.
 *
 * Reemplaza a `getEstadoLote`. Devuelve `null` cuando no hay nada que pintar: el
 * lote no se monitoreó, el catálogo todavía no cargó, o ninguna plaga de la
 * selección se pudo evaluar.
 *
 * **Un lote monitoreado donde ninguna plaga se pudo evaluar devuelve `null` y no
 * `"ok"`.** Pasa con los benéficos y con los organismos sin referencia: pintarlo
 * verde diría "lo revisamos y está bien" cuando lo que corresponde es "lo
 * revisamos y no sabemos".
 */
export function getEstadoLoteEvaluado(
  lote: LoteMonitoreado | undefined,
  seleccion: SeleccionPlagas,
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
): MonitoreoEstado | null {
  if (!lote) return null;
  if (plagasById.size === 0) return null;

  if (seleccion && seleccion.length > 0) {
    const conocidas = seleccion.filter((id) => plagasById.has(id));
    if (conocidas.length === 0) return null;
  }

  return getPeorPlagaEvaluada(lote, plagasById, seleccion)?.estado ?? null;
}

/** Cuántos lotes hay en cada estado del semáforo. */
export type ConteoEstados = {
  ok: number;
  warn: number;
  alert: number;
  /** Lotes de la finca que no se monitorearon en la selección activa, o cuyos
   *  organismos no se pudieron evaluar. */
  sinDatos: number;
};

/**
 * Cuenta los lotes por estado, para el resumen de la leyenda.
 *
 * Reemplaza a `contarEstados`. Recorre **todos los lotes de la finca**, no solo
 * los monitoreados: de ahí sale `sinDatos`, que es la resta entre unos y otros.
 *
 * Devuelve `null` mientras no haya catálogo, en vez de un conteo con todo en
 * `sinDatos`: ese cero diría que la finca entera está sin recorrer, que es
 * justamente la lectura equivocada que el semáforo trata de evitar.
 */
export function contarEstadosEvaluados(
  loteIds: string[],
  datos: DatosMonitoreo | null,
  seleccion: SeleccionPlagas,
  plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
): ConteoEstados | null {
  if (plagasById.size === 0) return null;

  const conteo: ConteoEstados = { ok: 0, warn: 0, alert: 0, sinDatos: 0 };

  for (const loteId of loteIds) {
    const estado = getEstadoLoteEvaluado(datos?.[loteId], seleccion, plagasById);
    if (estado === null) conteo.sinDatos += 1;
    else conteo[estado] += 1;
  }

  return conteo;
}

/** Largo a partir del cual un nombre de una sola palabra se recorta. */
const LARGO_SIN_RECORTE = 5;

/**
 * Etiqueta corta de una plaga, para encabezados angostos como los de la matriz
 * semáforo (una columna por plaga: con el nombre completo no entra ninguna).
 *
 * Prefiere la `abrev` del catálogo. Cuando el server no la definió, imita el
 * criterio con el que están escritas las que sí existen, en vez de reducir todo
 * a iniciales: una sola inicial ("Cochinilla" → "C.") no distingue nada si hay
 * dos plagas con la misma letra.
 *
 * - Una palabra: se recorta a cuatro letras ("Cochinilla" → "Coch.").
 *   Las cortas quedan enteras ("Broca").
 * - Varias: inicial de la primera + dos letras de la segunda
 *   ("Ácaro blanco" → "Á.Bl.").
 *
 * El nombre completo siempre queda en el `title` de la celda, así la
 * abreviatura nunca es la única forma de identificar la columna.
 */
export function getAbreviatura(plaga: MonitoreoPlagaEnfermedadItem): string {
  if (plaga.abrev) return plaga.abrev;

  const palabras = plaga.nombreComun.split(/\s+/).filter((p) => p.length > 0);
  if (palabras.length === 0) return plaga.nombreComun;

  if (palabras.length === 1) {
    const palabra = palabras[0];
    return palabra.length <= LARGO_SIN_RECORTE
      ? palabra
      : `${palabra.slice(0, 4)}.`;
  }

  const inicial = palabras[0][0].toUpperCase();
  const segunda =
    palabras[1][0].toUpperCase() + palabras[1].slice(1, 2).toLowerCase();
  return `${inicial}.${segunda}.`;
}

/** Indexa el catálogo de plagas por id, que es como lo consultan las tres
 *  vistas (las claves de `inc`/`datos` son ids de plaga). */
export function indexPlagasById(
  plagas: MonitoreoPlagaEnfermedadItem[],
): Map<string, MonitoreoPlagaEnfermedadItem> {
  return new Map(plagas.map((plaga) => [plaga.id, plaga]));
}

